import clsx from 'clsx';
import Flex from '@/components/ui/Flex';
import { useRef, useState } from 'react';
import { CircleCheck, LoaderCircle } from 'lucide-react';
import { honoPrivateClient } from '@/api/honoClient';
import usePaymentDetails from '@/store/paymentDetailsStore';

export default function CouponInput() {
  const { coupon, setCoupon } = usePaymentDetails();
  const [isFetchingCoupon, setFetchingCoupon] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const couponInputRef = useRef<HTMLInputElement>(null);

  async function onEnter(e: React.KeyboardEvent) {
    if (e.key === 'enter' && e.shiftKey == false) await handleOnSubmit();
  }

  async function handleOnSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    const couponCode = couponInputRef.current?.value;
    if (!couponCode) return;
    setFetchingCoupon(true);
    setErrorMessage(undefined);
    setCoupon(undefined);

    const response = await honoPrivateClient.coupon[':code'].$get({
      param: {
        code: couponCode,
      },
    });
    if (response.status === 200) {
      const data = await response.json();
      setCoupon({
        code: couponCode.toUpperCase(),
        type: data.discountType,
        discountValue: data.discountValue,
      });
    }
    if (response.status === 400) {
      const data = await response.json();
      switch (data.code) {
        case 'coupon_not_found':
          setErrorMessage('Promo code not found');
          break;
        case 'coupon_expired':
          setErrorMessage('Promo code expired');
          break;
        case 'coupon_use_limit_exceeded':
          setErrorMessage('Promo already used');
          break;
      }
    }
    setFetchingCoupon(false);
  }

  return (
    <Flex.Col className="gap-1">
      <div
        className={clsx(
          'pl-3 py-2 pr-1.5 gap-1',
          'bg-white rounded-lg border-gray-200 border',
          'focus-within:border-indigo-600 focus-within:ring focus-within:ring-indigo-600/15'
        )}
      >
        {!coupon ? (
          <form onSubmit={handleOnSubmit}>
            <Flex.Row className="justify-between items-center gap-2">
              <input
                className="outline-hidden text-sm h-7"
                ref={couponInputRef}
                placeholder="Promo code"
                maxLength={14}
                onKeyUp={onEnter}
              />
              <button
                className="py-1 px-2 text-sm bg-zinc-100 rounded-sm disabled:opacity-50"
                type="submit"
              >
                {!isFetchingCoupon ? (
                  'Apply'
                ) : (
                  <LoaderCircle className="animate-spin w-3.5 h-3.5" />
                )}
              </button>
            </Flex.Row>
          </form>
        ) : (
          <Flex.Row className="justify-between items-center gap-2">
            <Flex.Row className="gap-1 items-center w-full">
              <CircleCheck className="stroke-green-500" />
              <Flex.Row className="justify-between items-center w-full">
                <span className="text-sm">{coupon.code}</span>
                <span className="text-gray-600 text-sm">
                  -{coupon.discountValue}% off
                </span>
              </Flex.Row>
            </Flex.Row>
            <button
              className="py-1 px-2 text-sm bg-zinc-100 rounded-sm"
              onClick={() => setCoupon(undefined)}
            >
              Remove
            </button>
          </Flex.Row>
        )}
      </div>
      {errorMessage && (
        <span className="pl-1 text-sm text-rose-500">{errorMessage}</span>
      )}
    </Flex.Col>
  );
}
